import React, { useCallback, useEffect, useRef, useState } from "react";
import { Issue } from "@/types/issues";
import { Status } from "@/types/status";
import { Project } from "@/utils/types";
import { EstimateBadge } from "@/components/common/badges/estimate-badge";
import { LabelBadge } from "@/components/common/badges/label-badge";
import AssigneeDropdown from "@/components/common/issues/assignee-dropdown";
import { ContextMenuTrigger } from "@/components/ui/context-menu";
import { ContextMenu } from "@/components/ui/context-menu";
import { IssueContextMenu } from "./issue-context-menu";
import DueDateBadge from "../badges/due-date-badge";
import { cn } from "@/lib/utils";
import { useFocusStore } from "@/store/zustand/focusStore";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { Checkbox } from "@/components/ui/checkbox";
import { PriorityBadge } from "@/components/common/badges/priority-badge";
import { shouldIgnoreMouseHover } from "@/hooks/useIssueKeyboardShortcuts";
import { StatusBadge } from "../badges/status-badge";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";

// Configure hover delay (in milliseconds)
const HOVER_DELAY = 100;

interface IssueListProps {
  issue: Issue;
  project: Project;
  canvas: any;
  statuses: Status[];
  onSelect: (project: Project, canvas: any) => void;
  icon?: any;
  issueRefs?: { [key: string]: HTMLDivElement };
  visibleIssueIds?: string[]; // For selection range calculations
  provided?: any; // Drag and drop provided prop
  snapshot?: any; // Drag and drop snapshot prop
}

export const IssueList = React.forwardRef<HTMLDivElement, IssueListProps>(
  (
    {
      issue,
      project,
      canvas,
      statuses,
      onSelect,
      icon,
      issueRefs,
      visibleIssueIds,
      provided,
      snapshot,
    },
    ref
  ) => {
    const setLastFocusedIssue = useFocusStore(
      (state) => state.setLastFocusedIssue
    );
    const focusedIssueId = useFocusStore((state) => state.focusedIssueId);

    const projectLabels = Array.isArray(project?.labels)
      ? project.labels
      : ([] as any[]);

    // The number of labels to show before starting to stack
    const LABEL_STACK_THRESHOLD = 5;
    // Once stacking starts, the number of labels to show before the stack
    const PRE_STACK_LABEL_COUNT = 4;

    const [isKeyboardActive, setIsKeyboardActive] = useState(false);

    useEffect(() => {
      const handleKeyDown = () => setIsKeyboardActive(true);
      const handleMouseMove = () => setIsKeyboardActive(false);

      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("mousemove", handleMouseMove);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("mousemove", handleMouseMove);
      };
    }, []);

    const isFocused = focusedIssueId === issue.canvas.id;

    // Issue selection hook
    const selectedIssueIds = useIssueSelectionStore(
      (state) => state.selectedIssueIds
    );
    const toggleIssueSelection = useIssueSelectionStore(
      (state) => state.toggleIssueSelection
    );
    const isSelected = selectedIssueIds.includes(issue.canvas.id);

    // Refs for hover tracking
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isHoveredRef = useRef(false);

    // Clear hover timeout on unmount
    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    const handleMouseEnter = useCallback(() => {
      // Skip hover focus if keyboard operation is active
      if (shouldIgnoreMouseHover()) return;

      isHoveredRef.current = true;
      // Start the delay timer only on enter
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        if (isHoveredRef.current && !shouldIgnoreMouseHover()) {
          setLastFocusedIssue(issue.canvas.id);
          // Clear the timeout ref to indicate timer has completed
          hoverTimeoutRef.current = null;
        }
      }, HOVER_DELAY);
    }, [issue.canvas.id]);

    const handleMouseMove = useCallback(() => {
      // Skip hover focus if keyboard operation is active
      if (shouldIgnoreMouseHover()) return;

      // If timer has expired (null) and we're not already focused on this issue,
      // set focus immediately
      if (!hoverTimeoutRef.current && focusedIssueId !== issue.canvas.id) {
        setLastFocusedIssue(issue.canvas.id);
      }
    }, [focusedIssueId, issue.canvas.id]);

    const handleMouseLeave = useCallback(() => {
      isHoveredRef.current = false;
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    }, []);

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        // Don't trigger click during drag
        if (snapshot?.isDragging) {
          e.preventDefault();
          return;
        }

        // Set last focused issue on any direct interaction
        setLastFocusedIssue(issue.canvas.id);
        onSelect(project, canvas);
      },
      [issue.canvas.id, onSelect, project, canvas, snapshot?.isDragging]
    );

    const handleCheckboxChange = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleIssueSelection(issue.canvas.id);
      },
      [toggleIssueSelection, issue.canvas.id]
    );

    return (
      <div className="flex flex-col">
        {/* Issue Row */}
        <ContextMenu
          onOpenChange={(open) => {
            // Don't open context menu if we're dragging
            if (snapshot?.isDragging) return;

            if (open) {
              useProjectModalStore
                .getState()
                .addProjectModal("issue-context-menu");
            } else {
              useProjectModalStore
                .getState()
                .removeProjectModal("issue-context-menu");
            }
          }}
        >
          <ContextMenuTrigger
            asChild
            onContextMenu={(e) => {
              // Prevent context menu during drag
              if (snapshot?.isDragging) {
                e.preventDefault();
                return;
              }
              setLastFocusedIssue(issue.canvas.id);
            }}
          >
            <div
              ref={(el) => {
                // Handle the forwarded ref
                if (typeof ref === "function") {
                  ref(el);
                } else if (ref) {
                  ref.current = el;
                }
                // Store in our issue refs map
                if (el && issueRefs) {
                  issueRefs[issue.canvas.id] = el;
                }
                // Handle drag and drop ref
                if (provided?.innerRef) {
                  provided.innerRef(el);
                }
              }}
              {...provided?.draggableProps}
              {...provided?.dragHandleProps}
              className={cn(
                "w-full flex items-center justify-between min-h-9 pr-9 pl-3 py-2 group cursor-pointer transition-all duration-200",
                // Focus styling - subtle ring for keyboard navigation
                isFocused &&
                  "outline outline-1 outline-offset-[-1px] outline-primary-n-600",
                isFocused && !isSelected && "bg-secondary-n-950",
                // Hover styling (different for selected vs unselected)
                !isSelected && !isKeyboardActive && "hover:bg-secondary-100",
                isSelected && "bg-primary-n-600/20",
                // Drag and drop styling
                snapshot?.isDragging &&
                  "rotate-1 shadow-xl scale-105 z-50 border border-border bg-background",
                // Prevent text selection during drag
                snapshot?.isDragging && "select-none"
              )}
              data-issue-id={issue.canvas.id}
              data-focused={isFocused ? "true" : "false"}
              data-selected={isSelected ? "true" : "false"}
              onMouseEnter={handleMouseEnter}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
            >
              {/* Checkbox Column */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <div
                  className="flex items-center justify-center w-4 h-4"
                  onClick={handleCheckboxChange}
                >
                  <Checkbox
                    checked={isSelected}
                    className={cn(
                      "h-4 w-4 rounded-sm border text-white",
                      "data-[state=checked]:bg-background-accent data-[state=checked]:border-border-light",
                      "border-secondary-n-600 hover:border-primary-400",
                      "hover:ring-1 hover:ring-primary-n-600 hover:ring-offset-1"
                    )}
                  />
                </div>
                {/* Priority and Status Icons */}
                <div className="flex items-center gap-1">
                  <PriorityBadge
                    priority={issue.featurePrd?.priority || 0}
                    issueId={issue.canvas.id}
                    hasOutline={false}
                  />
                  <StatusBadge
                    status={
                      statuses.find(
                        (s) => s.name === issue.featurePrd?.status
                      ) ?? statuses[0]
                    }
                    issueId={issue.canvas.id}
                    statuses={statuses}
                  />
                </div>
              </div>

              {/* Issue Content - Left side: Feature Name */}
              <div className="flex items-center gap-1 pl-1 min-w-0 flex-1">
                {/* Feature Name */}
                <span className="body text-xs font-medium">
                  {issue.featurePrd?.title || ""}
                </span>
              </div>

              {/* Right side: Label Badge, Estimate Badge, Due Date Badge, Assignee */}
              <div className="flex items-center gap-3 ml-4">
                {/* Label Badge */}
                {issue.canvas.labels && issue.canvas.labels.length > 0 ? (
                  issue.canvas.labels.length <= LABEL_STACK_THRESHOLD ? (
                    issue.canvas.labels.map((label) => (
                      <LabelBadge
                        key={label}
                        labelId={label}
                        issue={issue}
                        projectLabels={projectLabels}
                      />
                    ))
                  ) : (
                    <>
                      {issue.canvas.labels
                        .slice(0, PRE_STACK_LABEL_COUNT)
                        .map((label) => (
                          <LabelBadge
                            key={label}
                            labelId={label}
                            issue={issue}
                            projectLabels={projectLabels}
                          />
                        ))}
                      <LabelBadge
                        key={issue.canvas.labels[0]}
                        labelId={issue.canvas.labels[0]}
                        issue={issue}
                        projectLabels={projectLabels}
                        labelsToStack={(
                          issue.canvas.labels.map((label) =>
                            projectLabels?.find((l) => l.id === label)
                          ) ?? []
                        ).slice(PRE_STACK_LABEL_COUNT)}
                      />
                    </>
                  )
                ) : (
                  <></>
                )}

                {/* Estimate Badge */}
                {issue.featurePrd?.estimate &&
                  issue.featurePrd.estimate > 0 && (
                    <EstimateBadge
                      estimate={issue.featurePrd.estimate}
                      issueId={issue.canvas.id}
                    />
                  )}

                {/* Due Date Badge - last badge before Assignee */}
                {issue.featurePrd?.due_date &&
                  issue.featurePrd.due_date !== "" && (
                    <DueDateBadge
                      dueDate={issue.featurePrd.due_date}
                      issueId={issue.canvas.id}
                    />
                  )}

                {/* Assignee */}
                <AssigneeDropdown
                  issueId={issue.canvas.id}
                  users={Array.isArray(project?.users) ? project.users : []}
                  value={issue.canvas.assigned_user_id ?? null}
                />
              </div>
            </div>
          </ContextMenuTrigger>
          <IssueContextMenu
            issueIds={
              selectedIssueIds.length > 0 ? selectedIssueIds : [issue.canvas.id]
            }
            users={
              Array.isArray(project?.users) ? project.users : ([] as any[])
            }
            labels={
              Array.isArray(project?.labels) ? project.labels : ([] as any[])
            }
            status={statuses}
          />
        </ContextMenu>
      </div>
    );
  }
);

IssueList.displayName = "IssueList";
